import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  changePassword,
  resetChangePasswordStatus,
  logoutUser,
} from "../../API_Service/Login_Slice";
import InputField from "../../Common/InputField";
import { UserID } from "../../Common/UIComponents";

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, changePasswordStatus, error } = useSelector(
    (state) => state.auth,
  );

  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (changePasswordStatus === "success") {
      const timer = setTimeout(() => {
        onClose();
        dispatch(logoutUser()).then(() => {
          navigate("/login", { replace: true });
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [changePasswordStatus, onClose, dispatch, navigate]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setFieldErrors({});
      dispatch(resetChangePasswordStatus());
    }
  }, [isOpen, dispatch]);

  // Auto close on success
  useEffect(() => {
    if (changePasswordStatus === "success") {
      const timer = setTimeout(() => onClose(), 1500);
      return () => clearTimeout(timer);
    }
  }, [changePasswordStatus, onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const errors = {};
    if (!form.oldPassword.trim())
      errors.oldPassword = "Old password is required.";
    if (!form.newPassword.trim())
      errors.newPassword = "New password is required.";
    else if (form.newPassword.length < 6)
      errors.newPassword = "Password must be at least 6 characters.";
    else if (form.newPassword === form.oldPassword) {
      errors.newPassword =
        "New password cannot be the same as the old password.";
    }
    if (!form.confirmPassword.trim())
      errors.confirmPassword = "Please confirm your new password.";
    else if (form.newPassword !== form.confirmPassword)
      errors.confirmPassword = "Passwords do not match.";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const userId = UserID;
    dispatch(
      changePassword({
        userId,
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
      }),
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl mx-4 bg-white rounded-2xl border border-outline-variant shadow-xl overflow-hidden">
        {/* Accent bar */}
        <div className="h-1.5 w-full bg-primary" />

        <div className="p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-on-surface">
                Change Password
              </h2>
              <p className="text-sm text-on-surface-variant">
                Update your account password
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-surface-container text-on-surface-variant transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* Old Password — full width */}
          <InputField
            label="Old Password"
            type="password"
            value={form.oldPassword}
            onChange={(val) => handleChange("oldPassword", val)}
            placeholder="Enter old password"
            required
            error={fieldErrors.oldPassword}
          />

          {/* New + Confirm — side by side */}
          <div className="grid grid-cols-2 gap-6">
            <InputField
              key="new-password"
              label="New Password"
              type="password"
              value={form.newPassword}
              onChange={(val) => handleChange("newPassword", val)}
              placeholder="Enter new password"
              required
              error={fieldErrors.newPassword}
            />
            <InputField
              key="confirm-password"
              label="Confirm Password"
              type="password"
              value={form.confirmPassword}
              onChange={(val) => handleChange("confirmPassword", val)}
              placeholder="Enter confirm password"
              required
              error={fieldErrors.confirmPassword}
            />
          </div>

          {/* API success */}

          {changePasswordStatus === "success" && (
            <p className="text-green-600 text-sm flex items-center gap-2 bg-green-50 px-4 py-2.5 rounded-lg border border-green-200">
              <span className="material-symbols-outlined text-[18px]">
                check_circle
              </span>
              Password changed successfully!
            </p>
          )}

          {/* API error */}
          {changePasswordStatus === "error" && (
            <p className="text-red-600 text-sm flex items-center gap-2 bg-red-50 px-4 py-2.5 rounded-lg border border-red-200">
              <span className="material-symbols-outlined text-[18px]">
                error
              </span>
              {typeof error === "string"
                ? error
                : "Incorrect old password. Please try again."}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-semibold rounded-lg border border-outline-variant text-on-surface hover:bg-surface-container transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              {loading && (
                <span className="material-symbols-outlined text-[16px] animate-spin">
                  progress_activity
                </span>
              )}
              {loading ? "Changing..." : "Change Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
